// src/components/chat/SettingsMenuButton.tsx
import * as React from "react";
import { createLogger } from "../../utils/logger";
import { useChatSettings } from "../../contexts/ChatSettingsContext";
import { FEATURE_FLAGS } from "../../config/featureFlags";
import { useEntitlements } from "../../hooks/useEntitlements";
import SettingsIconSvg from "../../../assets/settings.svg";
import { track } from "../../utils/analytics";

const log = createLogger("SettingsMenuButton");

interface SettingsMenuButtonProps {
  className?: string;
  isStreaming?: boolean; // Whether AI is currently generating a response
}

/**
 * Settings button with dropdown menu for chat features
 * FIXED: Container-based click outside detection for reliable toggle functionality
 */
const SettingsMenuButton: React.FC<SettingsMenuButtonProps> = ({
  className = "",
  isStreaming = false,
}) => {
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const [isHovered, setIsHovered] = React.useState(false);

  const { conciseMode: isConciseModeEnabled } = useEntitlements();

  // CRITICAL FIX: Container ref that encompasses the entire component
  // This includes both the gear button AND the dropdown menu
  const containerRef = React.useRef<HTMLDivElement>(null);

  // Access chat settings context for Extended Thinking, Web Search, Composer Mode, and Benchmark Mode functionality
  const {
    settings,
    toggleExtendedThinking,
    toggleWebSearch,
    setWebSearch,
    toggleComposerMode,
    toggleBenchmarkMode,
    setBenchmarkMode,
    toggleConciseMode,
  } = useChatSettings();

  // FIXED: Click outside detection now uses container ref instead of button ref
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Now we check if the click is outside the ENTIRE component (button + dropdown)
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        log.debug("Click outside settings component detected, closing menu");
        setIsMenuOpen(false);
      } else {
        log.debug("Click inside settings component, keeping menu open");
      }
    };

    if (isMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isMenuOpen]);

  /**
   * Handle Extended Thinking toggle
   * ENHANCED: Provides immediate visual feedback while keeping menu open for multiple selections
   * This follows standard dropdown UX patterns and supports future multi-tool selection workflows
   */
  const handleExtendedThinkingToggle = () => {
    log.info("Extended Thinking toggle initiated", {
      currentState: settings.extendedThinking,
      willChangeTo: !settings.extendedThinking,
      menuBehavior: "stays open for additional selections",
    });

    // Perform the toggle immediately - this updates the checkmark color and triggers animation
    toggleExtendedThinking();

    // CRITICAL UX DECISION: Keep menu open for standard dropdown behavior
    // This allows users to:
    // 1. See their change take effect (visual feedback)
    // 2. Make additional selections without reopening menu
    // 3. Control when the interaction ends (user agency)
    // 4. Follow established patterns from other applications

    log.info("Extended Thinking toggle completed", {
      newState: !settings.extendedThinking,
      menuState: "remains open for user control",
      nextActions: "user can make additional selections or close manually",
    });
    track("settings:toggled", {
      setting: "extended_thinking",
      enabled: !settings.extendedThinking,
      source: "settings_menu",
    });
  };

  /**
   * Handle Web Search toggle
   * Auto-disables Benchmark Mode when disabling Web Search (dependency requirement)
   */
  const handleWebSearchToggle = () => {
    log.info("Web Search toggle initiated", {
      currentState: settings.webSearch,
      willChangeTo: !settings.webSearch,
      menuBehavior: "stays open for additional selections",
    });

    // Auto-disable Benchmark Mode when disabling Web Search (dependency)
    // Benchmark Mode requires Web Search to function properly
    if (settings.webSearch && settings.benchmarkMode) {
      setBenchmarkMode(false);
      log.info("Auto-disabled Benchmark Mode - requires Web Search", {
        webSearch: "disabling",
        benchmarkMode: "auto-disabled",
      });
    }

    // Perform the toggle immediately
    toggleWebSearch();

    log.info("Web Search toggle completed", {
      newState: !settings.webSearch,
      menuState: "remains open for user control",
    });

    track("settings:toggled", {
      setting: "web_search",
      enabled: !settings.webSearch,
      source: "settings_menu",
    });
  };

  /**
   * Handle Composer Mode toggle
   * Enables automatic application of auto-redline edits
   */
  const handleComposerModeToggle = () => {
    log.info("Composer Mode toggle initiated", {
      currentState: settings.composerMode,
      willChangeTo: !settings.composerMode,
      menuBehavior: "stays open for additional selections",
    });

    // Perform the toggle immediately
    toggleComposerMode();

    log.info("Composer Mode toggle completed", {
      newState: !settings.composerMode,
      menuState: "remains open for user control",
    });

    track("settings:toggled", {
      setting: "composer_mode",
      enabled: !settings.composerMode,
      source: "settings_menu",
    });
  };

  /**
   * Handle Benchmark Mode toggle
   * Enables automatic appending of industry best practices search parameters
   * Auto-enables Web Search when turning on Benchmark Mode (dependency requirement)
   */
  const handleBenchmarkModeToggle = () => {
    log.info("Benchmark Mode toggle initiated", {
      currentState: settings.benchmarkMode,
      willChangeTo: !settings.benchmarkMode,
      menuBehavior: "stays open for additional selections",
    });

    // Auto-enable Web Search when enabling Benchmark Mode (dependency)
    // Benchmark Mode requires Web Search to function properly
    if (!settings.benchmarkMode && !settings.webSearch) {
      setWebSearch(true);
      log.info("Auto-enabled Web Search - required for Benchmark Mode", {
        benchmarkMode: "enabling",
        webSearch: "auto-enabled",
      });
    }

    // Perform the toggle immediately
    toggleBenchmarkMode();

    log.info("Benchmark Mode toggle completed", {
      newState: !settings.benchmarkMode,
      menuState: "remains open for user control",
    });

    track("settings:toggled", {
      setting: "benchmark_mode",
      enabled: !settings.benchmarkMode,
      source: "settings_menu",
    });
  };

  /**
   * Handle Concise Mode toggle
   * Enables the concise writing style (backend injects formatting instructions:
   * executive summary + findings table instead of long prose).
   * The setting is snapshotted into each request when it is sent, so a toggle
   * during streaming never affects the in-flight response, only the next
   * message - during_streaming tracks how often users try exactly that.
   */
  const handleConciseModeToggle = () => {
    log.info("Concise Mode toggle initiated", {
      currentState: settings.conciseMode,
      willChangeTo: !settings.conciseMode,
      duringStreaming: isStreaming,
      menuBehavior: "stays open for additional selections",
    });

    toggleConciseMode();

    log.info("Concise Mode toggle completed", {
      newState: !settings.conciseMode,
      menuState: "remains open for user control",
    });

    track("settings:toggled", {
      setting: "concise_mode",
      enabled: !settings.conciseMode,
      during_streaming: isStreaming,
      source: "settings_menu",
    });
  };

  /**
   * Handle future features that aren't implemented yet
   * Enhanced logging for better debugging visibility
   */
  const handleComingSoonFeature = (featureName: string) => {
    log.info(`${featureName} clicked (coming soon feature)`, {
      menuBehavior: "stays open for consistent UX",
      futureImplementation: "will support multi-selection workflow",
    });

    // NOTE: Menu stays open for consistency with other menu items
    // When these features are implemented, users will expect the same behavior
    // as Extended Thinking (toggle with visual feedback, menu remains open)
  };

  /**
   * Toggle menu visibility
   * Enhanced with better logging to track menu state changes
   */
  const toggleMenu = () => {
    const newMenuState = !isMenuOpen;
    log.debug("Settings menu toggle", {
      previousState: isMenuOpen ? "open" : "closed",
      newState: newMenuState ? "open" : "closed",
    });
    setIsMenuOpen(newMenuState);
  };

  // Settings/document icon component
  const SettingsIcon = () => <SettingsIconSvg width="20" height="20" />;

  /**
   * Active checkmark icon for enabled features
   * Clean, distinctive black checkmark that clearly indicates active state
   */
  const ActiveCheckmarkIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <polyline
        points="20,6 9,17 4,12"
        stroke="#000000"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  return (
    // CRITICAL FIX: Container div with ref that encompasses the entire component
    <div
      ref={containerRef} // This ref now covers button + dropdown
      style={{ position: "relative" }}
      className={className}
    >
      {/* Add CSS animations for smooth visual feedback */}
      <style>{`
        @keyframes checkmark-activate {
          0% { transform: scale(1); }
          50% { transform: scale(1.2); }
          100% { transform: scale(1); }
        }
      `}</style>

      {/* Settings button - no longer needs its own ref */}
      <button
        onClick={toggleMenu}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        title="Search & Tools"
        aria-label="Search & Tools menu"
        style={{
          backgroundColor: isHovered ? "rgba(0, 102, 204, 0.1)" : "transparent",
          border: "none",
          borderRadius: "50%",
          width: "36px",
          height: "36px",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: isHovered ? "#0066cc" : "#666",
          transition: "all 0.2s ease",
        }}
      >
        <SettingsIcon />
      </button>

      {/* Dropdown Menu with clean checkmark display */}
      {isMenuOpen && (
        <div
          style={{
            position: "absolute",
            bottom: "100%",
            right: "-184px",
            marginBottom: "8px",
            backgroundColor: "white",
            borderRadius: "8px",
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
            border: "1px solid #e0e0e0",
            minWidth: "220px",
            maxWidth: "250px",
            zIndex: 1000,
            overflow: "hidden",
            fontFamily: "'Segoe UI', sans-serif",
          }}
        >
          {/* Extended Thinking Option - shows checkmark only when enabled */}
          <button
            onClick={handleExtendedThinkingToggle}
            style={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "12px 16px",
              backgroundColor: "transparent",
              border: "none",
              cursor: "pointer",
              transition: "background-color 0.2s ease",
              borderBottom: "1px solid #f0f0f0",
              textAlign: "left",
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLElement).style.backgroundColor = "#f9f9f9";
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLElement).style.backgroundColor = "transparent";
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "2px",
                pointerEvents: "none",
              }}
            >
              <span
                style={{
                  fontSize: "14px",
                  color: "#333",
                  fontWeight: settings.extendedThinking ? 500 : 400,
                  pointerEvents: "none",
                }}
              >
                Extended Thinking
              </span>
              <span
                style={{
                  fontSize: "12px",
                  color: "#666",
                  fontStyle: "normal",
                  pointerEvents: "none",
                }}
              >
                For more complex tasks
              </span>
            </div>

            {/* Clean checkmark display - only show when enabled */}
            <div style={{ pointerEvents: "none" }}>
              {settings.extendedThinking && <ActiveCheckmarkIcon />}
            </div>
          </button>

          {/* Search the web Option - now functional! */}
          <button
            onClick={handleWebSearchToggle}
            style={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "12px 16px",
              backgroundColor: "transparent",
              border: "none",
              cursor: "pointer",
              transition: "background-color 0.2s ease",
              borderBottom: "1px solid #f0f0f0",
              textAlign: "left",
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLElement).style.backgroundColor = "#f9f9f9";
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLElement).style.backgroundColor = "transparent";
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "2px",
                pointerEvents: "none",
              }}
            >
              <span
                style={{
                  fontSize: "14px",
                  color: "#333",
                  fontWeight: settings.webSearch ? 500 : 400,
                  pointerEvents: "none",
                }}
              >
                Search the web
              </span>
              <span
                style={{
                  fontSize: "12px",
                  color: "#666",
                  fontStyle: "normal",
                  pointerEvents: "none",
                }}
              >
                Access real-time information
              </span>
            </div>

            {/* Clean checkmark display - only show when enabled */}
            <div style={{ pointerEvents: "none" }}>
              {settings.webSearch && <ActiveCheckmarkIcon />}
            </div>
          </button>

          {/* Composer mode Option - now functional! */}
          <button
            onClick={isStreaming ? undefined : handleComposerModeToggle}
            disabled={isStreaming}
            title={isStreaming ? "Cannot toggle during AI response" : undefined}
            style={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "12px 16px",
              backgroundColor: "transparent",
              border: "none",
              cursor: isStreaming ? "not-allowed" : "pointer",
              transition: "background-color 0.2s ease",
              borderBottom: "1px solid #f0f0f0",
              textAlign: "left",
              opacity: isStreaming ? 0.5 : 1,
            }}
            onMouseEnter={(e) => {
              if (!isStreaming) {
                (e.currentTarget as HTMLElement).style.backgroundColor = "#f9f9f9";
              }
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLElement).style.backgroundColor = "transparent";
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "2px",
                pointerEvents: "none",
              }}
            >
              <span
                style={{
                  fontSize: "14px",
                  color: isStreaming ? "#999" : "#333",
                  fontWeight: settings.composerMode ? 500 : 400,
                  pointerEvents: "none",
                }}
              >
                Composer mode
              </span>
              <span
                style={{
                  fontSize: "12px",
                  color: isStreaming ? "#aaa" : "#666",
                  fontStyle: "normal",
                  pointerEvents: "none",
                }}
              >
                {isStreaming ? "Locked during response" : "Auto-apply redline edits"}
              </span>
            </div>

            {/* Clean checkmark display - only show when enabled */}
            <div style={{ pointerEvents: "none" }}>
              {settings.composerMode && <ActiveCheckmarkIcon />}
            </div>
          </button>

          {/* Benchmark Mode Option - search industry best practices */}
          {FEATURE_FLAGS.ENABLE_BENCHMARK_MODE && (
            <button
              onClick={handleBenchmarkModeToggle}
              style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "12px 16px",
                backgroundColor: "transparent",
                border: "none",
                cursor: "pointer",
                transition: "background-color 0.2s ease",
                borderBottom: "1px solid #f0f0f0",
                textAlign: "left",
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLElement).style.backgroundColor = "#f9f9f9";
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.backgroundColor = "transparent";
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "2px",
                  pointerEvents: "none",
                }}
              >
                <span
                  style={{
                    fontSize: "14px",
                    color: "#333",
                    fontWeight: settings.benchmarkMode ? 500 : 400,
                    pointerEvents: "none",
                  }}
                >
                  Benchmark Mode
                </span>
                <span
                  style={{
                    fontSize: "12px",
                    color: "#666",
                    fontStyle: "normal",
                    pointerEvents: "none",
                  }}
                >
                  Search industry best practices
                </span>
              </div>

              {/* Clean checkmark display - only show when enabled */}
              <div style={{ pointerEvents: "none" }}>
                {settings.benchmarkMode && <ActiveCheckmarkIcon />}
              </div>
            </button>
          )}

          {/* Concise Mode Option - shorter, focused responses. Hidden for
              tenants whose default response style already covers this ground,
              where a second compression control would only conflict with it. */}
          {isConciseModeEnabled && (
            <button
              onClick={handleConciseModeToggle}
              style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "12px 16px",
                backgroundColor: "transparent",
                border: "none",
                cursor: "pointer",
                transition: "background-color 0.2s ease",
                textAlign: "left",
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLElement).style.backgroundColor = "#f9f9f9";
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.backgroundColor = "transparent";
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "2px",
                  pointerEvents: "none",
                }}
              >
                <span
                  style={{
                    fontSize: "14px",
                    color: "#333",
                    fontWeight: settings.conciseMode ? 500 : 400,
                    pointerEvents: "none",
                  }}
                >
                  Concise Writing Style
                </span>
                <span
                  style={{
                    fontSize: "12px",
                    color: "#666",
                    fontStyle: "normal",
                    pointerEvents: "none",
                  }}
                >
                  Concise, to-the-point answers
                </span>
              </div>

              {/* Clean checkmark display - only show when enabled */}
              <div style={{ pointerEvents: "none" }}>
                {settings.conciseMode && <ActiveCheckmarkIcon />}
              </div>
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default SettingsMenuButton;
